import { useContext, useCallback, createContext } from 'react';

import {
  clientErrorTracker,
  useSoyuzClientConfig,
  useSoyuzFlag
} from '@spotahome/soyuz/client';

function getRecaptchaEnterprise() {
  const g = window?.grecaptcha;
  return g?.enterprise;
}

const ReCaptchaContext = createContext(null);

export function ReCaptchaProvider({ children }) {
  const { recaptchaSiteKey } = useSoyuzClientConfig();

  const executeRecaptcha = useCallback(
    async action => {
      if (!recaptchaSiteKey) {
        throw new Error('Missing reCAPTCHA site key.');
      }

      const api = getRecaptchaEnterprise();

      if (!api?.execute) {
        throw new Error('reCAPTCHA Enterprise API not available.');
      }

      if (!action) {
        throw new Error(
          'Missing reCAPTCHA action. Pass an action to executeRecaptcha(action).'
        );
      }

      return new Promise((resolve, reject) => {
        api.ready(() => {
          api.execute(recaptchaSiteKey, { action }).then(resolve).catch(reject);
        });
      });
    },
    [recaptchaSiteKey]
  );

  return (
    <ReCaptchaContext.Provider value={{ executeRecaptcha }}>
      {children}
    </ReCaptchaContext.Provider>
  );
}

export function useReCaptcha(action) {
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');
  const { executeRecaptcha } = useContext(ReCaptchaContext);

  return {
    executeRecaptcha: async () => {
      if (TenantRecaptchaFlag.isOff()) {
        return null;
      }

      let recaptchaToken = null;

      try {
        recaptchaToken = await executeRecaptcha(action);
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: `ReCaptchaContext-useExecuteRecaptcha-${action}`
        });
      }

      return recaptchaToken;
    }
  };
}

export default ReCaptchaProvider;
